/**
 * The right-hand "stage" of the Studio tab (desktop): one trending song, shown inside the Recent creations panel (same
 * bordered container, `p-4`) so its top edge lines up with the first row of trend cards. Song first so the primary
 * action sits above the fold: ← Back to Recent creations; the song row (cover with play / pause in the shared player,
 * title, artist · genre, rank + movement, status and the "TikTok only" chip — its tooltip carries the rights
 * explanation); "Remix" — the pane's only solid button, it opens the Remix dialog; then a full-width 9:16 TikTok player of the song's
 * top videos (prev / "n / N" / next underneath; the cover treatment of the cards when there are none); then the 30-day
 * sparkline and a quiet "Details" link to the song drawer. The page keys it by trend so the selected video resets
 * when another song is opened.
 */
import { type CSSProperties, useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ChevronLeft, ChevronRight, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/components/ui/utils';
import { claimTikTokPlayer, releaseTikTokPlayer, useTikTokPlayer } from '@/components/tiktok/playerSlot';
import { TikTokPausedPoster } from '@/components/tiktok/TikTokPausedPoster';
import { LazyTikTokEmbed } from '@/components/tiktok/LazyTikTokEmbed';
import { TIKTOK_PLAY_ALLOW, tiktokPreviewEmbedUrl } from '@/components/tiktok/embedUrl';
import { PlayButton, usePlayer } from './AudioPlayer';
import { CoverFallback, GenreTile } from './CoverArt';
import { trendHasVideoSource, useTrendVideos } from './queries';
import { RankChange, RightsChip, TrendSparkline, TrendStatusText } from './TrendBits';
import { trendGenres, trendPlayable, trendSparkline, type TrendFocus } from './trendInsights';

/**
 * The 9:16 video is as tall as the viewport allows: the song row, Remix, pager, chart and Details (~26rem), the app
 * header and gaps (~6rem) and the mini player (`--player-inset`, set on the panel) stay in view; never narrower than 200px.
 */
const VIDEO_FRAME_STYLE: CSSProperties = {
  maxWidth: 'max(200px, calc((100dvh - 32rem - var(--player-inset, 0px)) * 9 / 16))',
};

export function TrendStage({ focus, onBack, onRemix, onDetails }: {
  focus: TrendFocus;
  onBack: () => void;
  /** "Remix": open the Remix dialog for this song. */
  onRemix: () => void;
  onDetails: () => void;
}) {
  const t = focus.trend;
  const genres = trendGenres(t);
  const spark = useMemo(() => trendSparkline(t.history), [t.history]);
  const cover = focus.placeholderCover ? null : t.cover_url;

  return (
    <section aria-label={`Trend: ${t.title}`} className="flex flex-col gap-4 p-4">
      <div>
        <button type="button" onClick={onBack} className="inline-flex h-7 items-center gap-1.5 rounded-[8px] text-[12.5px] text-[#475467] hover:text-[#101828]" style={{ fontWeight: 500 }}>
          <ArrowLeft className="h-4 w-4" aria-hidden /> Back to Recent creations
        </button>
      </div>

      {/* 1. The song, and what to do with it — above the fold. */}
      <div data-stage-part="song" className="flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <PlayButton item={trendPlayable(t)} coverUrl={cover} fallback={<GenreTile genre={genres[0] ?? null} title={t.title} size="thumb" />} />
          <div className="min-w-0 flex-1">
            <h2 className="truncate text-[14px] leading-5 text-[#101828]" style={{ fontWeight: 600 }} title={t.title}>{t.title}</h2>
            <p className="truncate text-[12.5px] leading-[18px] text-[#667085]">{[t.artist || 'Unknown artist', genres[0]].filter(Boolean).join(' · ')}</p>
            <div className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-[11.5px] leading-4 text-[#667085]">
              <span className="inline-flex items-center gap-1 tabular-nums" style={{ fontWeight: 600 }}>#{t.rank} <RankChange v={t.rank_change} /></span>
              <TrendStatusText status={t.trend_status} />
              {t.cml_cleared && <RightsChip kind="tiktok" />}
            </div>
          </div>
        </div>
        <Button type="button" onClick={onRemix} aria-haspopup="dialog" title="Make an original track in this song’s style: tempo, instruments, energy" className="h-10 gap-1.5 rounded-[8px] bg-[#be43ff] text-[14px] text-white hover:bg-[#a835e0]">
          <Wand2 className="h-4 w-4" aria-hidden />
          Remix
        </Button>
      </div>

      {/* 2. How the song is used on TikTok. */}
      <StageVideo focus={focus} cover={cover} genre={genres[0] ?? null} />

      {/* 3. The curve and the full details. */}
      {spark && (
        <div data-stage-part="chart" title={spark.kind === 'views' ? 'Daily views, last 30 days' : 'Daily rank, last 30 days (up is better)'}>
          <div className="mb-1 text-[11.5px] leading-4 text-[#98a2b3]">{spark.kind === 'views' ? 'Daily views · 30 days' : 'Chart rank · 30 days'}</div>
          <TrendSparkline values={spark.values} width={300} height={32} fluid label={spark.kind === 'views' ? `Daily views for ${t.title}` : `Daily rank for ${t.title} (up is better)`} />
        </div>
      )}
      <button type="button" onClick={onDetails} className="self-center rounded-[8px] px-2 py-1 text-[12.5px] text-[#667085] underline-offset-2 hover:text-[#344054] hover:underline">
        Details
      </button>
    </section>
  );
}

function StageVideo({ focus, cover, genre }: { focus: TrendFocus; cover: string | null; genre: string | null }) {
  const t = focus.trend;
  const inline = t.top_videos ?? [];
  const loaded = useTrendVideos(t.trend_id, focus.region, inline.length === 0);
  const videos = inline.length ? inline : loaded.data ?? [];
  const [index, setIndex] = useState(0);
  const { isPlaying, pauseResume } = usePlayer();
  const video = videos[Math.min(index, Math.max(0, videos.length - 1))];

  // The page's single TikTok player (playerSlot.ts): opening this song takes it; a card started meanwhile takes it
  // back and this player falls back to its poster until the viewer presses play again.
  const holdsPlayer = useTikTokPlayer('stage');
  useEffect(() => {
    claimTikTokPlayer('stage');
    return () => releaseTikTokPlayer('stage');
  }, [t.trend_id]);

  const select = (i: number) => {
    if (i === index || i < 0 || i >= videos.length) return;
    if (isPlaying) pauseResume();
    claimTikTokPlayer('stage');
    setIndex(i);
  };

  if (!video) {
    return (
      <div data-stage-part="video" className="flex flex-col gap-2">
        <div className="mx-auto aspect-[9/16] w-full overflow-hidden rounded-[12px]" style={VIDEO_FRAME_STYLE}>
          {loaded.isLoading
            ? <div role="status" aria-label="Loading top TikTok videos" className="h-full w-full animate-pulse bg-[#f2f4f7]" />
            : <CoverFallback coverUrl={cover} genre={genre} title={t.title} />}
        </div>
        <p className="text-center text-[12.5px] leading-[18px] text-[#98a2b3]">
          {loaded.isLoading ? 'Loading top TikTok videos…'
            : trendHasVideoSource(t.trend_id) ? 'No TikTok videos found for this song yet.'
            : 'Top videos appear once TikTok is connected.'}
        </p>
      </div>
    );
  }

  const i = videos.indexOf(video);
  return (
    <div data-stage-part="video" className="flex flex-col gap-2">
      {/* Full width of the pane: the song row and "Remix" sit above it, so they stay in view. */}
      <div className="mx-auto aspect-[9/16] w-full overflow-hidden rounded-[12px] bg-[#f2f4f7]" style={VIDEO_FRAME_STYLE}>
        {/* One iframe at a time: the key remounts it for the newly selected video. */}
        {holdsPlayer ? (
          <LazyTikTokEmbed
            key={video.video_id}
            src={tiktokPreviewEmbedUrl(video, { autoplay: true })}
            title={`Top TikTok video ${i + 1} using ${t.title}`}
            lazy={false}
            allow={TIKTOK_PLAY_ALLOW}
            className="h-full w-full bg-[#f2f4f7]"
            iframeClassName="h-full w-full border-0"
          />
        ) : (
          <TikTokPausedPoster thumbnailUrl={video.thumbnail_url} label={`Play top TikTok video ${i + 1} using ${t.title}`} onPlay={() => claimTikTokPlayer('stage')} />
        )}
      </div>
      {videos.length > 1 && (
        <div className="flex items-center justify-center gap-3">
          <StageArrow dir="prev" disabled={i === 0} onClick={() => select(i - 1)} />
          <span className="min-w-10 text-center text-[12.5px] leading-[18px] tabular-nums text-[#667085]" aria-live="polite" aria-label={`Video ${i + 1} of ${videos.length}`}>{i + 1} / {videos.length}</span>
          <StageArrow dir="next" disabled={i >= videos.length - 1} onClick={() => select(i + 1)} />
        </div>
      )}
    </div>
  );
}

function StageArrow({ dir, disabled, onClick }: { dir: 'prev' | 'next'; disabled: boolean; onClick: () => void }) {
  const Icon = dir === 'prev' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={dir === 'prev' ? 'Previous video' : 'Next video'}
      className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] border border-[#eaecf0] bg-white text-[#475467] hover:bg-[#f9fafb] disabled:opacity-30')}
    >
      <Icon className="h-4 w-4" aria-hidden />
    </button>
  );
}
